import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, User, Clock, Share2, Heart, ArrowRight, BookOpen } from 'lucide-react';

// Blog articles data
const articlesData = {
  'interior-design-trends-2025': {
    title: 'Top 10 Interior Design Trends for 2025',
    excerpt: 'Discover the latest interior design trends that are reshaping modern homes.',
    category: 'Interior Design',
    date: 'Jan 15, 2025',
    author: 'Priya Sharma',
    authorRole: 'Senior Interior Designer',
    image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=85&w=1200',
    readTime: '5 min read',
    content: `
      <p>As we step into 2025, the interior design landscape continues to evolve, reflecting our changing lifestyles and priorities. This year, we're seeing a beautiful blend of sustainability, technology, and timeless elegance.</p>
      
      <h2>1. Biophilic Design Takes Center Stage</h2>
      <p>The connection between indoor spaces and nature has never been stronger. Expect to see more living walls, indoor gardens, and natural materials like stone, wood, and bamboo. This trend isn't just about aesthetics—it's about creating healthier living environments.</p>
      
      <h2>2. Warm Minimalism</h2>
      <p>Gone are the stark, cold minimalist spaces of the past. 2025 embraces "warm minimalism"—clean lines softened by warm earth tones, natural textures, and comfortable furniture. Think creamy whites, terracotta, and sage greens.</p>
      
      <h2>3. Smart Home Integration</h2>
      <p>Technology is becoming seamlessly integrated into our homes. Smart lighting, automated blinds, and voice-controlled systems are no longer add-ons but built-in features that blend invisibly into the design.</p>
      
      <h2>4. Curved Furniture</h2>
      <p>Soft, rounded shapes are replacing sharp angles. Curved sofas, arched doorways, and circular rugs create a sense of flow and comfort. This trend adds visual interest while making spaces feel more welcoming.</p>
      
      <h2>5. Sustainable Materials</h2>
      <p>Eco-consciousness is driving design choices. Recycled materials, upcycled furniture, and locally sourced elements are in demand. Homeowners want spaces that are not only beautiful but also environmentally responsible.</p>
      
      <h2>6. Bold Color Accents</h2>
      <p>While neutral bases remain popular, 2025 introduces bold color accents. Deep blues, rich burgundies, and vibrant oranges are appearing in statement pieces, artwork, and accent walls.</p>
      
      <h2>7. Multifunctional Spaces</h2>
      <p>With remote work here to stay, homes need to serve multiple purposes. Flexible furniture, convertible rooms, and clever storage solutions help create adaptable living spaces.</p>
      
      <h2>8. Artisanal and Handcrafted Elements</h2>
      <p>Mass production is giving way to appreciation for craftsmanship. Handmade pottery, custom furniture, and artisanal textiles add character and uniqueness to modern homes.</p>
      
      <h2>9. Wellness-Focused Design</h2>
      <p>Dedicated spaces for yoga, meditation, and exercise are becoming standard. Design elements that promote mental and physical well-being—from air-purifying plants to calming color palettes—are priorities.</p>
      
      <h2>10. Vintage Modern Fusion</h2>
      <p>The perfect blend of old and new creates spaces with soul. Mixing mid-century modern pieces with contemporary design, or incorporating antique finds into minimalist settings, is a defining trend of 2025.</p>
      
      <h2>Conclusion</h2>
      <p>The interior design trends of 2025 reflect our desire for homes that are sustainable, comfortable, and technologically advanced while remaining warm and personal. Whether you're renovating or just refreshing your space, these trends offer endless inspiration.</p>
    `,
    relatedArticles: ['property-buyers-guide', 'sustainable-interior-design', 'kitchen-design-trends']
  },
  'property-buyers-guide': {
    title: "How to Choose the Right Property: A Buyer's Guide",
    excerpt: 'Essential tips for first-time home buyers in India.',
    category: 'Real Estate',
    date: 'Jan 12, 2025',
    author: 'Rajesh Kumar',
    authorRole: 'Real Estate Expert',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=85&w=1200',
    readTime: '7 min read',
    content: `
      <p>Buying a home is one of the most significant financial decisions you'll make. This comprehensive guide will help you navigate the process with confidence.</p>
      
      <h2>Understanding Your Requirements</h2>
      <p>Before you start looking at properties, clearly define your needs. Consider the number of bedrooms, proximity to work, schools, hospitals, and public transportation. Think about both current needs and future plans.</p>
      
      <h2>Setting a Realistic Budget</h2>
      <p>Calculate how much you can afford, including down payment, EMIs, registration fees, and maintenance costs. A common rule is that your EMI shouldn't exceed 40% of your monthly income.</p>
      
      <h2>Location Analysis</h2>
      <p>Research neighborhoods thoroughly. Consider factors like:</p>
      <ul>
        <li>Infrastructure development plans</li>
        <li>Crime rates and safety</li>
        <li>Water and electricity supply reliability</li>
        <li>Future appreciation potential</li>
        <li>Quality of nearby schools and hospitals</li>
      </ul>
      
      <h2>Legal Due Diligence</h2>
      <p>Never skip legal verification. Ensure the property has clear title, all necessary approvals, and no pending litigation. Get a lawyer to review all documents.</p>
      
      <h2>RERA Verification</h2>
      <p>Check if the project is RERA registered. This provides transparency and accountability from builders, protecting your investment.</p>
      
      <h2>Builder Reputation</h2>
      <p>Research the builder's track record. Visit their completed projects, speak to existing residents, and check for any legal disputes.</p>
      
      <h2>Home Loan Pre-Approval</h2>
      <p>Get pre-approved for a home loan before house hunting. This gives you a clear budget and strengthens your negotiating position.</p>
      
      <h2>Site Visits</h2>
      <p>Visit properties at different times of the day. Check for noise levels, natural light, ventilation, and the overall condition of the building.</p>
      
      <h2>Hidden Costs to Consider</h2>
      <p>Account for registration fees (5-7% of property value), GST on under-construction properties, maintenance deposits, parking charges, and interior costs.</p>
      
      <h2>Negotiation Tips</h2>
      <p>Don't hesitate to negotiate. Research market rates, point out any issues during site visits, and ask for additional amenities or discounts.</p>
      
      <h2>Conclusion</h2>
      <p>Buying a home requires patience and thorough research. Take your time, seek professional advice, and don't let emotions drive your decision. The right property is worth waiting for.</p>
    `,
    relatedArticles: ['interior-design-trends-2025', 'rera-guide', 'tier2-cities-investment']
  },
  'sustainable-interior-design': {
    title: 'Sustainable Interior Design: Eco-Friendly Materials Guide',
    excerpt: 'Explore sustainable materials and practices for creating beautiful, environmentally responsible interiors.',
    category: 'Interior Design',
    date: 'Jan 8, 2025',
    author: 'Vikram Patel',
    authorRole: 'Sustainability Consultant',
    image: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?q=85&w=1200',
    readTime: '8 min read',
    content: `
      <p>Sustainable interior design is no longer just a trend—it's a responsibility. This guide explores eco-friendly materials and practices that don't compromise on style.</p>
      
      <h2>Understanding Sustainable Design</h2>
      <p>Sustainable interior design considers the environmental impact of materials, manufacturing processes, and the long-term durability of products. It's about making choices that are good for your home and the planet.</p>
      
      <h2>Bamboo: The Versatile Wonder</h2>
      <p>Bamboo grows incredibly fast and requires no pesticides. It's perfect for flooring, furniture, and decorative items. Look for FSC-certified bamboo products.</p>
      
      <h2>Reclaimed Wood</h2>
      <p>Using reclaimed wood gives character to your space while preventing deforestation. It's ideal for flooring, accent walls, and furniture.</p>
      
      <h2>Natural Fabrics</h2>
      <p>Choose organic cotton, linen, hemp, and wool for upholstery and curtains. These materials are biodegradable and produced with fewer chemicals.</p>
      
      <h2>Low-VOC Paints</h2>
      <p>Volatile Organic Compounds in traditional paints can harm indoor air quality. Low-VOC or zero-VOC paints are healthier alternatives.</p>
      
      <h2>Cork Flooring</h2>
      <p>Harvested from bark without harming trees, cork is renewable, comfortable underfoot, and naturally antimicrobial.</p>
      
      <h2>Recycled Materials</h2>
      <p>From recycled glass countertops to furniture made from reclaimed materials, there are endless options for incorporating recycled elements.</p>
      
      <h2>Energy-Efficient Lighting</h2>
      <p>LED lights use significantly less energy and last longer than traditional bulbs. Maximize natural light with strategic window placement.</p>
      
      <h2>Local Sourcing</h2>
      <p>Choosing locally made products reduces transportation emissions and supports local artisans and businesses.</p>
      
      <h2>Conclusion</h2>
      <p>Creating a sustainable home doesn't mean sacrificing style. With thoughtful material choices and design decisions, you can create beautiful spaces that tread lightly on the earth.</p>
    `,
    relatedArticles: ['interior-design-trends-2025', 'kitchen-design-trends', 'color-psychology']
  },
  'kitchen-design-trends': {
    title: 'Kitchen Design Trends: Modern vs Traditional',
    excerpt: 'Compare modern minimalist kitchens with traditional designs.',
    category: 'Interior Design',
    date: 'Dec 28, 2024',
    author: 'Sneha Gupta',
    authorRole: 'Kitchen Design Specialist',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=85&w=1200',
    readTime: '6 min read',
    content: `
      <p>The kitchen is the heart of any home. Whether you prefer sleek modern designs or warm traditional aesthetics, understanding both styles helps you make the right choice.</p>
      
      <h2>Modern Kitchen Characteristics</h2>
      <p>Modern kitchens feature clean lines, minimal ornamentation, and a focus on functionality. Key elements include:</p>
      <ul>
        <li>Handleless cabinets with push-to-open mechanisms</li>
        <li>Flat-panel doors with glossy or matte finishes</li>
        <li>Integrated appliances for a seamless look</li>
        <li>Monochromatic color schemes</li>
        <li>Sleek, thin countertops</li>
      </ul>
      
      <h2>Traditional Kitchen Characteristics</h2>
      <p>Traditional kitchens evoke warmth and timelessness with:</p>
      <ul>
        <li>Raised-panel cabinet doors</li>
        <li>Decorative moldings and corbels</li>
        <li>Natural wood finishes</li>
        <li>Ornate hardware</li>
        <li>Classic materials like marble and granite</li>
      </ul>
      
      <h2>Finding Your Style</h2>
      <p>Consider your home's architecture, your cooking habits, and personal preferences. Many homeowners opt for transitional designs that blend modern efficiency with traditional warmth.</p>
      
      <h2>Budget Considerations</h2>
      <p>Modern kitchens can be cost-effective with laminate finishes, while traditional kitchens often require premium materials. However, both styles offer options across price ranges.</p>
      
      <h2>Conclusion</h2>
      <p>There's no right or wrong choice—the best kitchen is one that suits your lifestyle and makes you happy to cook in every day.</p>
    `,
    relatedArticles: ['interior-design-trends-2025', 'sustainable-interior-design', 'color-psychology']
  },
  'rera-guide': {
    title: "Understanding RERA: A Homebuyer's Essential Guide",
    excerpt: 'Everything you need to know about RERA regulations.',
    category: 'Real Estate',
    date: 'Dec 25, 2024',
    author: 'Rajesh Kumar',
    authorRole: 'Real Estate Expert',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=85&w=1200',
    readTime: '8 min read',
    content: `
      <p>The Real Estate (Regulation and Development) Act, 2016 (RERA) has transformed the Indian real estate sector. Understanding its provisions empowers you as a buyer.</p>
      
      <h2>What is RERA?</h2>
      <p>RERA is a regulatory framework that aims to protect homebuyers and boost investments in the real estate industry through transparency and accountability.</p>
      
      <h2>Key Benefits for Buyers</h2>
      <ul>
        <li>Mandatory project registration ensures transparency</li>
        <li>Standardized carpet area definition</li>
        <li>Timely project delivery with penalties for delays</li>
        <li>Defect liability period of 5 years</li>
        <li>Grievance redressal mechanism</li>
      </ul>
      
      <h2>How to Verify RERA Registration</h2>
      <p>Visit your state's RERA website to check project registration status. Look for the RERA number on all project advertisements and documents.</p>
      
      <h2>Your Rights Under RERA</h2>
      <p>As a buyer, you have the right to accurate project information, possession on agreed dates, and refund with interest for delays.</p>
      
      <h2>Filing Complaints</h2>
      <p>If developers violate RERA provisions, you can file complaints with the RERA authority. The process is straightforward and designed to protect buyer interests.</p>
      
      <h2>Conclusion</h2>
      <p>RERA has significantly improved buyer protection in Indian real estate. Always verify RERA compliance before investing in any property.</p>
    `,
    relatedArticles: ['property-buyers-guide', 'tier2-cities-investment', 'property-management-roi']
  },
  'color-psychology': {
    title: 'Color Psychology in Interior Design',
    excerpt: 'How colors affect mood and productivity.',
    category: 'Interior Design',
    date: 'Dec 22, 2024',
    author: 'Priya Sharma',
    authorRole: 'Senior Interior Designer',
    image: 'https://images.unsplash.com/photo-1615529182904-14819c35db37?q=85&w=1200',
    readTime: '5 min read',
    content: `
      <p>Colors profoundly impact our emotions, productivity, and overall well-being. Understanding color psychology helps create spaces that serve your needs.</p>
      
      <h2>Blue: Calm and Productive</h2>
      <p>Blue promotes calmness and concentration. It's ideal for bedrooms and home offices. Light blues create spaciousness, while deep blues add sophistication.</p>
      
      <h2>Green: Balance and Harmony</h2>
      <p>Green represents nature and balance. It's versatile, working well in any room. Sage greens are calming, while emerald adds drama.</p>
      
      <h2>Yellow: Energy and Happiness</h2>
      <p>Yellow stimulates mental activity and creativity. Best used as accents, it brightens kitchens and playrooms without overwhelming.</p>
      
      <h2>Red: Passion and Appetite</h2>
      <p>Red energizes and stimulates appetite. Use it sparingly in dining areas or as accent pieces. It's too stimulating for bedrooms.</p>
      
      <h2>Neutrals: Flexibility and Sophistication</h2>
      <p>Whites, beiges, and grays provide flexibility for layering colors and textures. They create timeless backdrops for any style.</p>
      
      <h2>Practical Tips</h2>
      <ul>
        <li>Test paint samples in different lighting</li>
        <li>Consider the room's function</li>
        <li>Balance bold colors with neutrals</li>
        <li>Use the 60-30-10 rule for color distribution</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>The right colors transform spaces and influence how you feel at home. Don't be afraid to experiment—your home should reflect your personality.</p>
    `,
    relatedArticles: ['interior-design-trends-2025', 'sustainable-interior-design', 'kitchen-design-trends']
  }
};

// Article ID mapping from index
const articleIdMapping = [
  'interior-design-trends-2025',
  'property-buyers-guide',
  'property-management-roi',
  'sustainable-interior-design',
  'tier2-cities-investment',
  'smart-home-integration',
  'kitchen-design-trends',
  'rera-guide',
  'color-psychology'
];

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);

  const article = articlesData[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white py-32 text-center" data-testid="blog-not-found">
        <h1 className="text-2xl font-bold mb-4">Article not found</h1>
        <Button onClick={() => navigate('/blogs')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Articles
        </Button>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const relatedArticles = (article.relatedArticles || [])
    .map(id => ({ id, ...articlesData[id] }))
    .filter(a => a.title);

  return (
    <div className="min-h-screen bg-white py-24" data-testid="blog-detail-page">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-6 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/blogs')}
          className="text-gray-600 hover:text-gray-900"
          data-testid="back-to-blogs"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Articles
        </Button>
      </div>

      <article className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-[#F5F5F0] text-primary text-sm font-medium rounded-full">
              {article.category}
            </span>
            <span className="text-sm text-gray-500">{article.readTime}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="blog-detail-title">
            {article.title}
          </h1>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{article.author}</p>
                <p className="text-sm text-gray-500">{article.authorRole}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {article.date}
              </span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="mb-10">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-80 md:h-96 object-cover rounded-2xl"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-8">
          <Button
            variant="outline"
            onClick={() => setLiked(!liked)}
            className={liked ? 'text-red-500 border-red-500' : ''}
            data-testid="like-article-btn"
          >
            <Heart className={`w-5 h-5 mr-2 ${liked ? 'fill-current' : ''}`} />
            {liked ? 'Liked' : 'Like'}
          </Button>
          <Button variant="outline" onClick={handleShare} data-testid="share-article-btn">
            <Share2 className="w-5 h-5 mr-2" />
            Share
          </Button>
        </div>

        {/* Article Content — sanitized to prevent XSS */}
        <div 
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(article.content, {
              ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
                             'strong', 'em', 'a', 'blockquote', 'code', 'pre', 'br',
                             'img', 'figure', 'figcaption', 'hr', 'b', 'i'],
              ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'class'],
              ALLOW_DATA_ATTR: false,
            }),
          }}
        />

        {/* CTA */}
        <div className="bg-[#F5F5F0] rounded-2xl p-8 mb-12 text-center">
          <h3 className="text-2xl font-bold mb-2">Ready to Transform Your Space?</h3>
          <p className="text-gray-600 mb-6">
            Get a personalized AI-powered cost estimate for your interior design project.
          </p>
          <Button
            size="lg"
            className="bg-primary"
            onClick={() => navigate('/interior-design')}
            data-testid="article-cta-btn"
          >
            Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/blogs/${related.id}`}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`related-article-${related.id}`}
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs px-2 py-1 bg-[#F5F5F0] text-primary font-medium rounded">
                      {related.category}
                    </span>
                    <h3 className="font-semibold text-gray-900 mt-2 line-clamp-2">
                      {related.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogDetail;
